// @ts-strict-ignore
import {
	addExperimentGroupToTargeting,
	SlotsDefinitionRepository,
	SlotSetupDefinition,
} from '@platforms/shared';
import {
	AdSlotClass,
	AdSlotStatus,
	communicationService,
	context,
	InstantConfigService,
	RepeatableSlotPlaceholderConfig,
	scrollListener,
	slotPlaceholderInjector,
	UapLoadStatus,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { filter } from 'rxjs/operators';
import { AD_ENGINE_STACK_START } from '../../../../communication/events/events-ad-engine';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../../../communication/events/events-ad-engine-uap';
import { activateFloorAdhesionOnFVPages } from './activate-floor-adhesion-on-fv-pages';
import { activateFloorAdhesionOnICVAndUAPPages } from './activate-floor-adhesion-on-icv-and-uap-pages';

// eslint-disable-next-line no-restricted-imports
import {
	isNotUapNorRoadblockV2,
	isUapOrRoadblockV2,
} from '../../../../core/utils/condition/roadblock-slots-visibility';
import { AD_ENGINE_SLOT_LOADED } from '../../../../communication/events/events-ad-engine-slot';

const logGroup = 'ucp-desktop-slots-definition-repository';

interface WindowWithConnatix extends Window {
	cnxInstance: {
		disableFloatingMode(): void;
	};
}

declare let window: WindowWithConnatix;

@Injectable()
export class UcpDesktopSlotsDefinitionRepository implements SlotsDefinitionRepository {
	constructor(protected instantConfig: InstantConfigService) {}

	getTopLeaderboardConfig(): SlotSetupDefinition {
		const slotName = 'top_leaderboard';
		const placeholderConfig = context.get(`slots.${slotName}.placeholder`);

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig,
				anchorSelector: '.top-leaderboard',
				insertMethod: 'prepend',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	getGalleryLeaderboardConfig(): SlotSetupDefinition {
		const slotName = 'gallery_leaderboard';
		const placeholderConfig = context.get(`slots.${slotName}.placeholder`);

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig,
				anchorSelector: '.gallery-leaderboard',
				insertMethod: 'prepend',
				classList: ['ad-slot'],
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	getMidBoxAdConfig(): SlotSetupDefinition {
		const slotName = 'mid_boxad';

		return {
			slotCreatorConfig: {
				slotName,
				/* Load this AdUnit in the same place as the OpenWeb unit */
				anchorSelector: '.sticky-modules-wrapper',
				insertMethod: 'before',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => {
				communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
					/* If any UAP is loaded, then don't load the mid_boxad unit */
					if (action.isLoaded || action.adProduct === 'ruap') {
						utils.logger(logGroup, `${slotName} disabled - UAP is loaded`);
						return;
					}
					/* Lazy load this unit since it'll be BTF (below the fold).
					 * BTF ad units are all the ones that are not in the viewport when the page is loaded.
					 * */
					context.push('events.pushOnScroll.ids', slotName);
				});
			},
		};
	}

	getTopBoxadConfig(): SlotSetupDefinition {
		if (!this.isRightRailApplicable()) {
			return;
		}

		const slotName = 'top_boxad';

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '.main-page-tag-rcs, #rail-boxad-wrapper',
				insertMethod: 'prepend',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	private isIncontentLeaderboardApplicable(): boolean {
		const icLbMaxWidth = 768;
		const pageContentSelector = 'main.page__main #content';

		return utils.getWidth(document.querySelector(pageContentSelector)) >= icLbMaxWidth;
	}

	getIncontentLeaderboardConfig(): SlotSetupDefinition {
		if (!this.isIncontentLeaderboardApplicable()) {
			return;
		}

		const slotName = 'incontent_leaderboard';
		const anchorSelector = this.isIncontentPlayerApplicable()
			? context.get('templates.incontentAnchorSelector').replace(/h([2-5])/gi, 'h$1:nth-of-type(2)')
			: context.get('templates.incontentAnchorSelector');
		const slotConfig: SlotSetupDefinition = {
			slotCreatorConfig: {
				slotName,
				placeholderConfig: {
					createLabel: true,
				},
				anchorSelector,
				anchorPosition: 'belowFirstViewport',
				avoidConflictWith: ['.ad-slot-icl'],
				insertMethod: 'before',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot', 'ad-slot-icl'],
			},
			slotCreatorWrapperConfig: {
				classList: ['ad-slot-placeholder', 'incontent-leaderboard', 'is-loading'],
			},
			activator: () => {
				communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
					if (isUapOrRoadblockV2(action, false)) {
						const iclPlaceholder = document.querySelector(`#${slotName}`);
						iclPlaceholder.parentElement.classList.add(AdSlotClass.HIDDEN_AD_CLASS);
					}
					context.push('events.pushOnScroll.ids', slotName);
				});
			},
		};

		if (context.get('templates.incontentHeadersExperiment')) {
			slotConfig.slotCreatorConfig.repeat = {
				index: 1,
				limit: 20,
				slotNamePattern: `${slotName}_{slotConfig.repeat.index}`,
				updateProperties: {
					adProduct: '{slotConfig.slotName}',
					'targeting.rv': '{slotConfig.repeat.index}',
					'targeting.pos': [slotName],
					bidBeforeRun: false,
				},
				updateCreator: {
					anchorSelector: '.incontent-leaderboard',
					avoidConflictWith: ['.ad-slot-icl', '#incontent_player'],
					insertMethod: 'append',
					placeholderConfig: {
						createLabel: false,
					},
				},
			};
			slotConfig.activator = () => {
				communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
					context.push('events.pushOnScroll.ids', slotName);
					if (isNotUapNorRoadblockV2(action, !action.isLoaded)) {
						this.injectIncontentAdsPlaceholders();
					}
				});
			};
		}

		return slotConfig;
	}

	private injectIncontentAdsPlaceholders(): void {
		const adSlotCategory = 'incontent';

		const iclPlaceholderConfig: RepeatableSlotPlaceholderConfig = {
			classList: ['ad-slot-placeholder', 'incontent-leaderboard', 'is-loading'],
			anchorSelector: context.get('templates.incontentAnchorSelector'),
			avoidConflictWith: ['.ad-slot-placeholder', '.incontent-leaderboard', '#incontent_player'],
			insertMethod: 'before',
			repeatStart: 1,
			repeatLimit: 19,
		};

		slotPlaceholderInjector.injectAndRepeat(iclPlaceholderConfig, adSlotCategory);
	}

	getIncontentBoxadConfig(): SlotSetupDefinition {
		if (!this.isRightRailApplicable()) {
			return;
		}

		const slotName = 'incontent_boxad';

		const noBiddersExperiment = this.instantConfig
			.get('icExperiments', [])
			.includes('icbNoBiddersExperiment');

		if (noBiddersExperiment) {
			utils.logger(logGroup, 'ICB no bidders experiment - enabled');
			addExperimentGroupToTargeting('icb-no-bidders-experiment-enabled');
		} else {
			utils.logger(logGroup, 'ICB no bidders experiment - disabled');
			addExperimentGroupToTargeting('icb-no-bidders-experiment-disabled');
		}

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '.sticky-modules-wrapper',
				insertMethod: 'append',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot', 'icb-out-of-popular-pages'],
			},
			activator: () => {
				communicationService.on(AD_ENGINE_STACK_START, () => {
					context.push('events.pushOnScroll.ids', slotName);
				});
			},
		};
	}

	private isRightRailApplicable(rightRailBreakingPoint = 1024): boolean {
		return utils.getViewportWidth() >= rightRailBreakingPoint;
	}

	getBottomLeaderboardConfig(): SlotSetupDefinition {
		const slotName = 'bottom_leaderboard';
		const placeholderConfig = context.get(`slots.${slotName}.placeholder`);

		return {
			slotCreatorConfig: {
				slotName,
				placeholderConfig,
				anchorSelector: '.bottom-leaderboard',
				insertMethod: 'prepend',
				classList: ['ad-slot'],
			},
			activator: () => {
				context.push('events.pushOnScroll.ids', slotName);
			},
		};
	}

	getIncontentPlayerConfig(): SlotSetupDefinition {
		const slotName = 'incontent_player';

		if (!this.isIncontentPlayerApplicable()) {
			return;
		}

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: context.get('templates.incontentAnchorSelector'),
				anchorPosition: 'belowFirstViewport',
				avoidConflictWith: ['.incontent-leaderboard'],
				insertMethod: 'before',
			},
			activator: () => {
				context.push('state.adStack', { id: slotName });
			},
		};
	}

	private isIncontentPlayerApplicable(): boolean {
		return context.get('custom.hasIncontentPlayer');
	}

	getFloorAdhesionConfig(): SlotSetupDefinition {
		if (this.instantConfig.get('icDisableDesktopFloorAdhesion')) {
			utils.logger(logGroup, 'Desktop floor adhesion disabled');
			return;
		}

		if (context.get('experiments.desktopDrawer')) return;

		const slotName = 'floor_adhesion';

		// Mark that floor adhesion will get activated
		document.body.classList.add('floor-adhesion-container');

		const activateFloorAdhesion = () => {
			const distance = 2 * utils.getViewportHeight() + window.scrollY;

			// If user is near the bottom of the page, no need to show the floor unit
			if (distance + window.innerHeight >= document.body.scrollHeight) {
				return;
			} else {
				scrollListener.addSlot(slotName, { distanceFromTop: distance });
			}
		};

		// Disable incontent player floating mode when floor adhesion loads and there is no featured video
		if (!this.hasFeaturedVideo()) {
			const ofType = (...types) => {
				return (source) => {
					return source.pipe(
						filter((action) => types.some((type) => (action as { type: string }).type === type)),
					);
				};
			};

			// Disable incontent player floating mode when floor adhesion loads
			communicationService.on(AD_ENGINE_SLOT_LOADED, (payload) => {
				if (payload.name == 'floor_adhesion' && payload.state === AdSlotStatus.STATUS_SUCCESS) {
					communicationService.action$.pipe(ofType('[AdEngine] Anyclip ready')).subscribe(() => {
						window?.anyclip?.getWidget()?.floatingModeToggle(false);
					});

					communicationService.action$.pipe(ofType('[AdEngine] Connatix ready')).subscribe(() => {
						window?.cnxInstance?.disableFloatingMode();
					});
				}
			});
		}

		return {
			slotCreatorConfig: {
				slotName,
				anchorSelector: '.page',
				insertMethod: 'before',
				classList: [AdSlotClass.HIDDEN_AD_CLASS, 'ad-slot'],
			},
			activator: () => {
				// Use the proper activator based on the page type
				if (this.hasFeaturedVideo()) {
					activateFloorAdhesionOnFVPages(activateFloorAdhesion);
				} else {
					activateFloorAdhesionOnICVAndUAPPages(activateFloorAdhesion);
				}
			},
		};
	}

	private hasFeaturedVideo(): boolean {
		return context.get('custom.hasFeaturedVideo');
	}
}
